import { LoggerService } from '@angular-ru/cdk/logger';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { IDataboxTimer } from 'src/app/api/modules/core/dynamic/databoxes/timer/IDataboxTimer';

/**
 * The template variable which is replaced by the hours value.
 */
const HOURS_TOKEN = '%H%';

/**
 * The template variable which is replaced by the minutes value.
 */
const MINUTES_TOKEN = '%M%';

/**
 * The template variable which is replaced by the seconds value.
 */
const SECONDS_TOKEN = '%S%';

/**
 * Displays a live game time inside a DataboxContainer.
 *
 * The configured `template` formats the seconds pushed in through
 * {@link update} using any combination of `%H%`, `%M%` and `%S%`; until the
 * first update arrives (or when a new config is applied) the `templateIdle`
 * string is shown. An unconfigured databox renders the built-in defaults.
 *
 * See {@link IDataboxTimer} for the configuration structure.
 */
@Component({
  selector: 'app-databox-timer',
  templateUrl: './databox-timer.component.html',
  styleUrl: './databox-timer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataboxTimerComponent {
  private readonly loggerService = inject(LoggerService);

  /**
   * The Config as provided to the element.
   *
   * Consumers may bind `null` / `undefined` sources; the built-in defaults
   * are used until a usable config arrives.
   */
  readonly config = input<IDataboxTimer | null>();

  /**
   * The last valid game time received via {@link update} (clamped to zero),
   * or `undefined` while idle (no update yet, or the config changed).
   */
  private readonly seconds = signal<number | undefined>(undefined);

  /**
   * The validated template strings, falling back to the defaults (and
   * collecting a warning, logged by an effect) when the config is unusable.
   */
  private readonly templates = computed(() => {
    const configuration = this.config();
    let warning: string | undefined;

    // the template is used to process the numeric values into a formatted time string.
    let template = `${HOURS_TOKEN}:${MINUTES_TOKEN}:${SECONDS_TOKEN}`;
    if (configuration?.template) {
      const usesAnyToken = [HOURS_TOKEN, MINUTES_TOKEN, SECONDS_TOKEN].some((token) => configuration.template.includes(token));
      if (usesAnyToken) {
        template = configuration.template;
      } else {
        warning = `you must use a ${HOURS_TOKEN}, ${MINUTES_TOKEN}, ${SECONDS_TOKEN} or some combination of these in your template. The default will be used.`;
      }
    }

    // template idle is shown until an acceptable value has been provided.
    const templateIdle = configuration?.templateIdle || '--:--:--';

    return { template, templateIdle, warning };
  });

  /**
   * The formatted string the template displays.
   */
  protected readonly output = computed(() => {
    const { template, templateIdle } = this.templates();
    const seconds = this.seconds();

    if (seconds === undefined) {
      return templateIdle;
    }

    return formatSecondsToTemplate(template, seconds);
  });

  constructor() {
    // surface configuration problems (once per config change)
    effect(() => {
      const warning = this.templates().warning;
      if (warning) {
        this.loggerService.warn(`[DataboxTimer] - ${warning}`);
      }
    });

    // a new config returns the databox to its idle output until the next update
    effect(() => {
      this.config();
      untracked(() => this.seconds.set(undefined));
    });
  }

  /**
   * Updates the component with the game time.
   *
   * A non-numeric value is ignored and the current output is kept; a negative
   * value is clamped to zero.
   *
   * @param seconds - the game time in seconds which will be formatted to the template
   */
  update(seconds?: number) {
    if (typeof seconds !== 'number') {
      this.loggerService.info('[DataboxTimer] update - No seconds value was provided for processing. Update ignored.');
      return;
    }
    this.seconds.set(Math.max(0, seconds));
  }
}

/**
 * Splices a seconds value into a time template using whichever of `%H%`,
 * `%M%`, `%S%` the template contains.
 *
 * The largest unit present absorbs the overflow (so `%M%:%S%` shows `90:00`
 * for 5400 seconds, and `%S%` alone shows the raw total). Every unit is
 * zero-padded to two digits, except when the template uses only a single
 * unit, which is shown without padding. Fractional seconds survive with up
 * to two decimal places.
 */
function formatSecondsToTemplate(template: string, seconds: number): string {
  const useHours = template.includes(HOURS_TOKEN);
  const useMinutes = template.includes(MINUTES_TOKEN);
  const useSeconds = template.includes(SECONDS_TOKEN);
  const usedCount = [useHours, useMinutes, useSeconds].filter(Boolean).length;

  const pad = (value: number) => (usedCount > 1 && value < 10 ? `0${value}` : value.toString());

  let remaining = seconds;
  let output = template;

  if (useHours) {
    const hours = Math.floor(remaining / 3600);
    remaining -= hours * 3600;
    output = output.replace(HOURS_TOKEN, pad(hours));
  }
  if (useMinutes) {
    const minutes = Math.floor(remaining / 60);
    remaining -= minutes * 60;
    output = output.replace(MINUTES_TOKEN, pad(minutes));
  }
  if (useSeconds) {
    const roundedSeconds = Math.round(remaining * 100) / 100;
    output = output.replace(SECONDS_TOKEN, pad(roundedSeconds));
  }

  return output;
}
